import React, { Component } from 'react';
import Modal from '../../components/Modal';
import { Icon } from '../../components/Icon';
import { urlMap, getAuthHeaders } from '../../../utils';
import RewritePlanIssueSuggestionsList from './RewritePlanIssueSuggestionsList';

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
const PRIORITY_LABELS = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

/**
 * RewritePlanModal
 *
 * Displays the rewrite plan from project.metadata.rewritePlan.
 * Supports editing the plan overview/actions and regenerating via PlanRewriteStep.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - project: object
 */
export default class RewritePlanModal extends Component {
  state = {
    editing: false,
    regenerating: false,
    error: null,

    // Editable fields
    editOverview: '',
    editActions: [],

    // UI state
    expandedActions: {},
    expandedIssueSuggestions: {},
    saving: false,
    userComment: '',
    issueDecisionTargets: {},
    issueDecisionErrors: {},
    localActionDecisionStatuses: {},
    localIssueDecisionStatuses: {},
  };

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.loadPlan();
    }
    if (!this.props.isOpen && prevProps.isOpen) {
      this.setState({
        editing: false,
        regenerating: false,
        error: null,
        expandedActions: {},
        expandedIssueSuggestions: {},
        saving: false,
        issueDecisionTargets: {},
        issueDecisionErrors: {},
        localActionDecisionStatuses: {},
        localIssueDecisionStatuses: {},
      });
    }
    // Reload when metadata changes while open
    if (
      this.props.isOpen &&
      prevProps.isOpen &&
      this.props.project?.metadata !== prevProps.project?.metadata &&
      !this.state.editing
    ) {
      this.loadPlan();
      // Clear all decision states when data refreshes
      this.setState({
        expandedIssueSuggestions: {},
        issueDecisionTargets: {},
        issueDecisionErrors: {},
        localActionDecisionStatuses: {},
        localIssueDecisionStatuses: {},
      });
    }
  }

  getPlan() {
    return this.props.project?.metadata?.rewritePlan || null;
  }

  loadPlan = () => {
    const plan = this.getPlan();
    if (plan) {
      this.setState({
        editOverview: plan.overview || '',
        editActions: (plan.actions || []).map((a) => ({ ...a })),
      });
    }
  };

  /* ── Actions ── */

  handleRegenerate = () => {
    const { project } = this.props;
    if (!project?.id) return;

    const extraData = {};
    if (this.state.userComment.trim()) {
      extraData.userComment = this.state.userComment.trim();
    }
    extraData.forceRerun = true;

    // Close modal, then trigger agent panel to run from PlanRewriteStep
    this.setState({ userComment: '' });
    this.props.onClose();
    this.props.onTriggerAgentStep?.('PlanRewriteStep', extraData);
  };

  handleEdit = () => {
    this.loadPlan();
    this.setState({ editing: true });
  };

  handleCreateManualPlan = () => {
    this.setState({
      editing: true,
      editOverview: '',
      editActions: [],
      error: null,
    });
  };

  handleCancelEdit = () => {
    this.loadPlan();
    this.setState({ editing: false });
  };

  handleSave = async () => {
    const { project } = this.props;
    if (!project?.id) return;

    this.setState({ saving: true, error: null });

    try {
      const existingPlan = this.getPlan() || {};
      const updatedPlan = {
        ...existingPlan,
        overview: this.state.editOverview,
        actions: this.state.editActions,
      };

      const url = `${urlMap.projectEndpoint}/${project.id}/metadata`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ rewritePlan: updatedPlan }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      await this.props.onRefreshProject?.();
      this.setState({ editing: false, saving: false });
    } catch (err) {
      this.setState({ error: err.message, saving: false });
    }
  };

  toggleActionExpanded = (actionId) => {
    this.setState((s) => ({
      expandedActions: { ...s.expandedActions, [actionId]: !s.expandedActions[actionId] },
    }));
  };

  toggleIssueExpanded = (issueId) => {
    this.setState((s) => ({
      expandedIssueSuggestions: {
        ...s.expandedIssueSuggestions,
        [issueId]: !s.expandedIssueSuggestions[issueId],
      },
    }));
  };

  performRewritePlanDecision = async (decisionKey, endpoint, payload = null) => {
     console.log('Performing decision', { decisionKey, endpoint, payload });

    
    this.setState((s) => ({
      issueDecisionTargets: { ...s.issueDecisionTargets, [decisionKey]: true },
      issueDecisionErrors: { ...s.issueDecisionErrors, [decisionKey]: null },
      error: null,
    }));

    try {
     
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: payload ? JSON.stringify(payload) : undefined,
      });

      if (response.status !== 200) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (!data?.success || !data?.id) throw new Error('INVALID_DECISION_RESPONSE');

      const decisionStatus = endpoint.includes('/approve') ? 'approved' : 'dismissed';
      const [decisionType] = String(decisionKey).split(':');
      const entityId = String(decisionKey).split(':')[1];

      this.setState((s) => {
        const nextState = {
          issueDecisionTargets: { ...s.issueDecisionTargets, [decisionKey]: false },
        };

        if (decisionType === 'action') {
          nextState.localActionDecisionStatuses = {
            ...s.localActionDecisionStatuses,
            [entityId]: decisionStatus,
          };
        } else if (decisionType === 'issue') {
          nextState.localIssueDecisionStatuses = {
            ...s.localIssueDecisionStatuses,
            [entityId]: decisionStatus,
          };
          if (decisionStatus === 'approved') {
            nextState.expandedIssueSuggestions = {
              ...s.expandedIssueSuggestions,
              [entityId]: false,
            };
          }
        }

        return nextState;
      });
      return true;
    } catch (err) {
      this.setState((s) => ({
        issueDecisionTargets: { ...s.issueDecisionTargets, [decisionKey]: false },
        issueDecisionErrors: { ...s.issueDecisionErrors, [decisionKey]: err.message },
      }));
      return false;
    }
  };

  renderChapterIssueSuggestions = (plan) => {
    const allSuggestions = (plan.chapterIssueSuggestions || []).filter((issue) => {
      const effectiveStatus = this.state.localIssueDecisionStatuses[issue.id] || issue.status || 'pending';
      return effectiveStatus !== 'dismissed';
    });

    // Deduplicate: keep only the suggestion with the lowest chapterNumber per unique type+title
    const baseTitle = (title) => String(title || '').replace(/\s*\(also in Ch[\s\d,]+\)\s*$/i, '').trim();
    const dedupeMap = new Map();
    for (const issue of allSuggestions) {
      const key = `${String(issue.type || '').toLowerCase()}::${baseTitle(issue.title).toLowerCase()}`;
      const existing = dedupeMap.get(key);
      const issueStatus = this.state.localIssueDecisionStatuses[issue.id] || issue.status || 'pending';
      const existingStatus = existing
        ? (this.state.localIssueDecisionStatuses[existing.id] || existing.status || 'pending')
        : null;

      if (!existing) {
        dedupeMap.set(key, issue);
        continue;
      }

      const issueApproved = issueStatus === 'approved';
      const existingApproved = existingStatus === 'approved';

      // Keep approved item visible when duplicates exist.
      if (issueApproved && !existingApproved) {
        dedupeMap.set(key, issue);
        continue;
      }

      if (issueApproved === existingApproved && issue.chapterNumber < existing.chapterNumber) {
        dedupeMap.set(key, issue);
      }
    }
    const suggestions = Array.from(dedupeMap.values());

    if (!suggestions.length) return null;

    const sortedSuggestions = [...suggestions].sort(
      (a, b) => {
        const statusRank = (status) => (status === 'approved' ? 0 : status === 'pending' ? 1 : 2);
        const aStatus = this.state.localIssueDecisionStatuses[a.id] || a.status || 'pending';
        const bStatus = this.state.localIssueDecisionStatuses[b.id] || b.status || 'pending';
        const byStatus = statusRank(aStatus) - statusRank(bStatus);
        if (byStatus !== 0) return byStatus;
        return (PRIORITY_ORDER[a.severity] ?? 9) - (PRIORITY_ORDER[b.severity] ?? 9);
      },
    );

    const items = sortedSuggestions.map((issue) => {
      const decisionKey = `issue:${issue.id}`;
      const effectiveStatus = this.state.localIssueDecisionStatuses[issue.id] || issue.status || 'pending';
      const chapterLabel = Number.isInteger(issue.chapterNumber) && issue.chapterNumber > 0
        ? `Ch ${issue.chapterNumber}`
        : 'Project';
      const relatedLabel = Array.isArray(issue.relatedChapterNumbers) && issue.relatedChapterNumbers.length > 0
        ? `Related: Ch ${issue.relatedChapterNumbers.join(', ')}`
        : String(issue.type || 'issue').replace(/_/g, ' ');

      return {
        id: issue.id,
        expandKey: decisionKey,
        severity: issue.severity || 'medium',
        severityLabel: PRIORITY_LABELS[issue.severity] || issue.severity,
        chapterLabel,
        title: issue.title,
        scopeLabel: relatedLabel,
        description: issue.description,
        suggestedFix: issue.suggestedFix,
        status: effectiveStatus,
        isBusy: !!this.state.issueDecisionTargets[decisionKey],
        error: this.state.issueDecisionErrors[decisionKey],
        onApprove: () => this.performRewritePlanDecision(
          decisionKey,
          urlMap.approveSuggestion(this.props.project.id, issue.id),
        ),
        onDismiss: () => this.performRewritePlanDecision(
          decisionKey,
          urlMap.dismissSuggestion(this.props.project.id, issue.id),
        ),
      };
    });

    return (
      <RewritePlanIssueSuggestionsList
        title="Chapter Issue Suggestions"
        items={items}
        expandedState={this.state.expandedIssueSuggestions}
        onToggleExpanded={this.toggleIssueExpanded}
      />
    );
  };

  renderActionTransformSuggestions = (plan) => {
    if (this.state.editing) return null;

    const candidateActions = (plan.actions || []).filter((action) => {
      if (!action?.id) return false;
      const isPlotHole = action.source === 'plotHoles';
      if (!isPlotHole && (!Number.isInteger(action.chapterNumber) || action.chapterNumber <= 0)) {
        return false;
      }
      const effectiveStatus = this.state.localActionDecisionStatuses[action.id] || action.holeTransformStatus || 'pending';
      return effectiveStatus !== 'dismissed';
    });

    if (!candidateActions.length) return null;

    const sortedActions = [...candidateActions].sort(
      (a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9),
    );

    const items = sortedActions.map((action) => {
      const decisionKey = `action:${action.id}`;
      const isPlotHole = action.source === 'plotHoles';
      const effectiveStatus = this.state.localActionDecisionStatuses[action.id] || action.holeTransformStatus || 'pending';
      const chapterLabel = Number.isInteger(action.chapterNumber) && action.chapterNumber > 0
        ? `Ch ${action.chapterNumber}`
        : 'Project';
      const helpText = isPlotHole
        ? 'Approve this plot issue to add it as a chapter issue.'
        : 'Turn this chapter-scoped rewrite action into a chapter issue.';

      return {
        id: action.id,
        expandKey: decisionKey,
        severity: action.priority || 'medium',
        severityLabel: PRIORITY_LABELS[action.priority] || action.priority,
        chapterLabel,
        title: action.title || 'Rewrite Action',
        scopeLabel: String(action.estimatedScope || 'rewrite_action').replace(/_/g, ' '),
        description: action.description,
        detailLines: [helpText],
        status: effectiveStatus,
        isBusy: !!this.state.issueDecisionTargets[decisionKey],
        error: this.state.issueDecisionErrors[decisionKey],
        approveLabel: effectiveStatus === 'approved' ? 'Approved Issue' : 'Approve Issue',
        dismissLabel: effectiveStatus === 'approved' ? 'Remove from List' : 'Dismiss Issue',
        onApprove: () => this.performRewritePlanDecision(
          decisionKey,
          urlMap.approveSuggestion(this.props.project.id, action.id),
        ),
        onDismiss: () => this.performRewritePlanDecision(
          decisionKey,
          urlMap.dismissSuggestion(this.props.project.id, action.id),
        ),
      };
    });

    return (
      <RewritePlanIssueSuggestionsList
        title="Actions to Transform"
        items={items}
        expandedState={this.state.expandedIssueSuggestions}
        onToggleExpanded={this.toggleIssueExpanded}
      />
    );
  };

  /* ── Edit helpers ── */

  updateAction = (index, field, value) => {
    this.setState((s) => {
      const actions = [...s.editActions];
      actions[index] = { ...actions[index], [field]: value };
      return { editActions: actions };
    });
  };

  removeAction = (index) => {
    this.setState((s) => ({
      editActions: s.editActions.filter((_, i) => i !== index),
    }));
  };

  addAction = () => {
    this.setState((s) => ({
      editActions: [
        ...s.editActions,
        {
          chapterNumber: 1,
          action: 'revise',
          priority: 'medium',
          title: '',
          description: '',
          rationale: '',
          affectedElements: [],
          estimatedScope: 'minor_edit',
        },
      ],
    }));
  };

  /* ── Render ── */

  renderEmpty() {
    return (
      <div className="rwp-empty">
        <Icon.RewritePlan style={{ width: 48, height: 48, opacity: 0.3 }} />
        <p>No action plan generated yet.</p>
        <p className="rwp-empty-hint">
          Run the agent to create an action plan based on your chapters.
        </p>
        <button
          type="button"
          className="btn primary"
          onClick={this.handleRegenerate}
          disabled={this.state.regenerating}
        >
          {this.state.regenerating ? 'Generating…' : 'Generate Plan'}
        </button>
        <button
          type="button"
          className="btn subtle"
          onClick={this.handleCreateManualPlan}
          disabled={this.state.regenerating}
        >
          Create by Hand
        </button>
      </div>
    );
  }

  renderActionCard(action, index) {
    const { editing, expandedActions, editActions } = this.state;
    const isExpanded = !!expandedActions[action.id];
    const data = editing ? editActions[index] || action : action;

    return (
      <div
        key={action.id}
        className={`rwp-action-card rwp-priority-${data.priority || 'medium'}`}
      >
        <div
          className="rwp-action-header"
          onClick={() => this.toggleActionExpanded(action.id)}
        >
          <span className={`rwp-priority-badge rwp-priority-${data.priority}`}>
            {PRIORITY_LABELS[data.priority] || data.priority}
          </span>
          <span className="rwp-action-chapter">
            {Number.isInteger(data.chapterNumber) && data.chapterNumber > 0 ? `Ch ${data.chapterNumber}` : 'Project'}
          </span>
          {editing ? (
            <input
              className="rwp-inline-input rwp-action-title-input"
              value={data.title || ''}
              onChange={(e) => this.updateAction(index, 'title', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Action title"
            />
          ) : (
            <span className="rwp-action-title">{data.title}</span>
          )}
          <span className="rwp-action-scope">{(data.estimatedScope || '').replace(/_/g, ' ')}</span>
          <span className="rwp-action-chevron">{isExpanded ? '▾' : '▸'}</span>
        </div>

        {isExpanded && (
          <div className="rwp-action-body">
            {editing ? (
              <>
                <label className="rwp-field-label">Description</label>
                <textarea
                  className="rwp-textarea"
                  value={data.description || ''}
                  onChange={(e) => this.updateAction(index, 'description', e.target.value)}
                  rows={3}
                />
                <label className="rwp-field-label">Rationale</label>
                <textarea
                  className="rwp-textarea"
                  value={data.rationale || ''}
                  onChange={(e) => this.updateAction(index, 'rationale', e.target.value)}
                  rows={2}
                />
                <div className="rwp-action-edit-row">
                  <label className="rwp-field-label">Priority</label>
                  <select
                    className="rwp-select"
                    value={data.priority || 'medium'}
                    onChange={(e) => this.updateAction(index, 'priority', e.target.value)}
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  <label className="rwp-field-label">Chapter</label>
                  <input
                    type="number"
                    className="rwp-inline-input rwp-chapter-input"
                    value={data.chapterNumber || ''}
                    onChange={(e) => this.updateAction(index, 'chapterNumber', parseInt(e.target.value, 10) || 0)}
                    min={1}
                  />
                  <button
                    type="button"
                    className="rwp-remove-btn"
                    onClick={() => this.removeAction(index)}
                    title="Remove action"
                  >
                    ✕
                  </button>
                </div>
              </>
            ) : (
              <>
                {data.description && (
                  <div className="rwp-action-desc">
                    <strong>What to change:</strong> {data.description}
                  </div>
                )}
                {data.rationale && (
                  <div className="rwp-action-rationale">
                    <strong>Why:</strong> {data.rationale}
                  </div>
                )}
                {data.affectedElements?.length > 0 && (
                  <div className="rwp-action-elements">
                    <strong>Affects:</strong>{' '}
                    {data.affectedElements.map((el, i) => (
                      <span key={i} className="rwp-element-tag">{el}</span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  renderPlan() {
    const plan = this.getPlan() || {
      overview: '',
      actions: [],
      newCharactersNeeded: [],
      chaptersToAdd: [],
      progressSummary: null,
      approvedStructuralCount: 0,
      approvedThematicCount: 0,
      criticalCount: 0,
      highCount: 0,
    };
    const {
      editing,
      editOverview,
      editActions,
    } = this.state;

    const overview = editing ? editOverview : plan.overview;
    const actions = editing ? editActions : plan.actions || [];
    const newChars = plan.newCharactersNeeded || [];
    const chaptersToAdd = plan.chaptersToAdd || [];

    const hasStructuralContext = (plan.approvedStructuralCount || 0) > 0;
    const hasThematicContext = (plan.approvedThematicCount || 0) > 0;

    const sortedActions = [...actions].sort(
      (a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9)
    );
    const actionIndexMap = Object.fromEntries(actions.map((action, index) => [action.id, index]));

    return (
      <div className="rwp-plan">
        {/* Context banner for approved structural/thematic changes */}
        {(hasStructuralContext || hasThematicContext) && (
          <div className="rwp-context-banner">
            ℹ This plan incorporates
            {hasStructuralContext && (
              <span> {plan.approvedStructuralCount} approved structural change(s)</span>
            )}
            {hasStructuralContext && hasThematicContext && <span> and</span>}
            {hasThematicContext && (
              <span> {plan.approvedThematicCount} approved thematic adjustment(s)</span>
            )}
            .
          </div>
        )}

        {/* Stats bar */}
        <div className="rwp-stats">
          <span className="rwp-stat">{actions.length} actions</span>
          {plan.criticalCount > 0 && (
            <span className="rwp-stat rwp-stat-critical">{plan.criticalCount} critical</span>
          )}
          {plan.highCount > 0 && (
            <span className="rwp-stat rwp-stat-high">{plan.highCount} high</span>
          )}
          {plan.rewritePlanGeneratedAt || this.props.project?.metadata?.rewritePlanGeneratedAt ? (
            <span className="rwp-stat rwp-stat-date">
              Generated {new Date(plan.rewritePlanGeneratedAt || this.props.project.metadata.rewritePlanGeneratedAt).toLocaleDateString()}
            </span>
          ) : null}
          <span style={{ flex: 1 }} />
          {!this.state.editing && (
            <button
              type="button"
              className="btn subtle"
              onClick={this.handleEdit}
              disabled={this.state.regenerating}
              title="Edit plan"
              style={{ padding: '2px 8px', fontSize: '0.8rem' }}
            >
              <Icon.Edit /> Edit
            </button>
          )}
          {this.state.editing && (
            <>
              <button
                type="button"
                className="btn subtle"
                onClick={this.handleCancelEdit}
                style={{ padding: '2px 8px', fontSize: '0.8rem' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={this.handleSave}
                disabled={this.state.saving}
                style={{ padding: '2px 8px', fontSize: '0.8rem' }}
              >
                {this.state.saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>

        {/* Progress Estimate */}
        {plan.progressSummary && (
          <div className="rwp-estimate-banner">
            <span className="rwp-estimate-label">Project Draft Completion:</span>
            <span className="rwp-estimate-value">{plan.progressSummary.projectProgressPercent}%</span>
            <span className="rwp-estimate-detail">(LLM estimate — adjusted by your work)</span>
          </div>
        )}

        {/* Overview */}
        <section className="rwp-section">
          <h3 className="rwp-section-title">Overview</h3>
          {editing ? (
            <textarea
              className="rwp-textarea rwp-overview-textarea"
              value={overview}
              onChange={(e) => this.setState({ editOverview: e.target.value })}
              rows={5}
            />
          ) : (
            <p className="rwp-overview">{overview}</p>
          )}
        </section>

        {this.renderActionTransformSuggestions(plan)}

        {this.renderChapterIssueSuggestions(plan)}

        {/* New Characters Needed */}
        {newChars.length > 0 && (
          <section className="rwp-section">
            <h3 className="rwp-section-title">New Characters Needed</h3>
            <div className="rwp-chars-list">
              {newChars.map((c, i) => (
                <div key={i} className="rwp-char-card">
                  <strong>{c.name}</strong> — {c.role}
                  {c.introducedInChapter && <span className="rwp-char-ch"> (Ch {c.introducedInChapter})</span>}
                  {c.purpose && <p className="rwp-char-purpose">{c.purpose}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Chapters to Add */}
        {chaptersToAdd.length > 0 && (
          <section className="rwp-section">
            <h3 className="rwp-section-title">Chapters to Add</h3>
            {chaptersToAdd.map((ch, i) => (
              <div key={i} className="rwp-chapter-add-card">
                <strong>After Chapter {ch.afterChapter}:</strong> {ch.title}
                {ch.purpose && <p>{ch.purpose}</p>}
                {ch.keyEvents?.length > 0 && (
                  <ul className="rwp-list">
                    {ch.keyEvents.map((ev, j) => (
                      <li key={j}>{ev}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </section>
        )}

      </div>
    );
  }

  renderInnerContent() {
    const { editing, regenerating, error, saving } = this.state;
    const plan = this.getPlan();

    return (
      <div className="rwp-modal-content">
        <div className="rwp-modal-header">
          <span className="rwp-modal-title">Rewrite Plan</span>
        </div>
        {error && <div className="rwp-error">{error}</div>}

        {regenerating ? (
          <div className="rwp-loading">
            <div className="agent-spinner" aria-hidden="true" />
            <span>Generating action plan…</span>
          </div>
        ) : (plan || editing) ? (
          this.renderPlan()
        ) : (
          this.renderEmpty()
        )}

        {!editing && !regenerating && plan && (
          <div className="rwp-comment-section">
            <div className="rwp-comment-label">Revision Notes</div>
            <div className="rwp-comment-row">
              <textarea
                className="rwp-comment-input"
                value={this.state.userComment}
                onChange={(e) => this.setState({ userComment: e.target.value })}
                placeholder="The action plan is just an opinion — ignore or adjust."
                rows={2}
              />
              <button
                type="button"
                className="btn primary rwp-submit-btn"
                onClick={this.handleRegenerate}
                disabled={regenerating || !this.state.userComment.trim()}
              >
                <Icon.Restart /> Submit
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { isOpen, onClose, embedded } = this.props;

    if (!isOpen) return null;

    if (embedded) return this.renderInnerContent();

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        {this.renderInnerContent()}
      </Modal>
    );
  }
}
